import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import useSWR from 'swr';
import { useShallow } from 'zustand/react/shallow';

import { useGradeFlow } from '@/states/grade-flow';
import { useUserProfile } from '@/states/user-profile';
import { getCaseImageLayerList, getCaseImagesData } from '@/api/cases';

export const useInitLoadLayerImages = () => {
  const { caseId } = useParams<{ caseId: string }>();

  const { profile } = useUserProfile();
  const isVitaziAdmin = profile?.role === 'vitaziadmin';

  // fetch the list of layer images
  const { data: layerListResponse, isLoading: isLoadingImages } = useSWR(
    caseId && isVitaziAdmin ? ['layer-images-list', caseId] : null,
    ([, caseId]) => getCaseImageLayerList(caseId),
    {
      revalidateOnFocus: false,
      // avoid to retry on 404 errors, as it means the case does not have layer images
      shouldRetryOnError: (err) => {
        if (err && typeof err === 'object' && err !== null) {
          const status = (err as { status?: number }).status;
          if (status === 404) return false;
        }
        return true;
      },
    }
  );

  const { data: imageSegmentationData, isLoading: isLoadingSegmentationData } = useSWR(
    caseId && isVitaziAdmin ? ['image-segmentation-data', caseId] : null,
    ([, caseId]) => getCaseImagesData(caseId),
    { revalidateOnFocus: false }
  );

  const layerImagesList = layerListResponse?.data;

  const {
    setLayerImages,
    setIsLoadingLayerImages,
    setImageSegmentationData,
    setIsLoadingImageSegmentationData,
  } = useGradeFlow(
    useShallow((s) => ({
      setLayerImages: s.setLayerImages,
      setIsLoadingLayerImages: s.setIsLoadingLayerImages,
      setImageSegmentationData: s.setImageSegmentationData,
      setIsLoadingImageSegmentationData: s.setIsLoadingImageSegmentationData,
    }))
  );

  useEffect(() => {
    setIsLoadingLayerImages(isLoadingImages);
  }, [isLoadingImages, setIsLoadingLayerImages]);

  useEffect(() => {
    setIsLoadingImageSegmentationData(isLoadingSegmentationData);
  }, [isLoadingSegmentationData, setIsLoadingImageSegmentationData]);

  // Update Zustand state when layer images are loaded
  useEffect(() => {
    if (layerImagesList?.length) {
      setLayerImages(layerImagesList);

      // preload the images so when the user clicks on the image the layer is already loaded
      layerImagesList.forEach((obj) => {
        const img = new Image();
        img.src = obj.url;
      });
    }
  }, [layerImagesList, setLayerImages]);

  // Update Zustand state when image segmentation data is loaded
  useEffect(() => {
    if (imageSegmentationData?.length) {
      setImageSegmentationData(imageSegmentationData);
    }
  }, [imageSegmentationData, setImageSegmentationData]);
};
